import { useState } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Chip from '@mui/joy/Chip'
import ChipDelete from '@mui/joy/ChipDelete'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import FormLabel from '@mui/joy/FormLabel'
import FormControl from '@mui/joy/FormControl'
import CircularProgress from '@mui/joy/CircularProgress'
import Alert from '@mui/joy/Alert'
import Divider from '@mui/joy/Divider'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PersonIcon from '@mui/icons-material/Person'
import TelegramIcon from '@mui/icons-material/Telegram'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import { CrmTeamMember, TeamMemberInput } from 'api/oracleApi'
import { useTeamMembers } from '../hooks/useTeamMembers'

interface MemberFormState {
    name: string
    tgInput: string
    telegram_handles: string[]
    emailInput: string
    emails: string[]
    phoneInput: string
    phone_numbers: string[]
}

const emptyForm = (): MemberFormState => ({
    name: '',
    tgInput: '',
    telegram_handles: [],
    emailInput: '',
    emails: [],
    phoneInput: '',
    phone_numbers: [],
})

function memberToForm(m: CrmTeamMember): MemberFormState {
    return { name: m.name, tgInput: '', telegram_handles: m.telegram_handles, emailInput: '', emails: m.emails, phoneInput: '', phone_numbers: m.phone_numbers }
}

function formToInput(f: MemberFormState): TeamMemberInput {
    return { name: f.name.trim(), telegram_handles: f.telegram_handles, emails: f.emails, phone_numbers: f.phone_numbers }
}

function MultiChipInput({
    label,
    placeholder,
    icon,
    values,
    inputValue,
    onInputChange,
    onAdd,
    onRemove,
    normalize,
}: {
    label: string
    placeholder: string
    icon: React.ReactNode
    values: string[]
    inputValue: string
    onInputChange: (v: string) => void
    onAdd: () => void
    onRemove: (v: string) => void
    normalize?: (v: string) => string
}) {
    const display = normalize ? (v: string) => normalize(v) : (v: string) => v
    return (
        <FormControl>
            <FormLabel>{label}</FormLabel>
            <Stack gap={0.5}>
                {values.length > 0 && (
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {values.map((v) => (
                            <Chip key={v} size="sm" endDecorator={<ChipDelete onDelete={() => onRemove(v)} />}>
                                {display(v)}
                            </Chip>
                        ))}
                    </Stack>
                )}
                <Stack direction="row" gap={1}>
                    <Input
                        size="sm"
                        startDecorator={icon}
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={(e) => onInputChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                onAdd()
                            }
                        }}
                        sx={{ flex: 1 }}
                    />
                    <Button size="sm" variant="outlined" onClick={onAdd}>
                        Add
                    </Button>
                </Stack>
            </Stack>
        </FormControl>
    )
}

interface MemberFormProps {
    initial: MemberFormState
    onSave: (input: TeamMemberInput) => Promise<void>
    onClose: () => void
    title: string
}

function MemberForm({ initial, onSave, onClose, title }: MemberFormProps) {
    const [form, setForm] = useState<MemberFormState>(initial)
    const [saving, setSaving] = useState(false)
    const [err, setErr] = useState('')

    const addItem = (
        field: 'telegram_handles' | 'emails' | 'phone_numbers',
        inputField: 'tgInput' | 'emailInput' | 'phoneInput',
        normalize: (v: string) => string
    ) => {
        const raw = form[inputField].trim()
        const val = normalize(raw)
        if (!val || form[field].includes(val)) {
            setForm((f) => ({ ...f, [inputField]: '' }))
            return
        }
        setForm((f) => ({ ...f, [field]: [...f[field], val], [inputField]: '' }))
    }

    const removeItem = (field: 'telegram_handles' | 'emails' | 'phone_numbers', val: string) => {
        setForm((f) => ({ ...f, [field]: f[field].filter((x) => x !== val) }))
    }

    const handleSave = async () => {
        if (!form.name.trim()) {
            setErr('Name is required')
            return
        }
        setSaving(true)
        setErr('')
        try {
            await onSave(formToInput(form))
            onClose()
        } catch {
            setErr('Failed to save. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <ModalDialog sx={{ minWidth: 440, maxWidth: 520 }}>
            <ModalClose />
            <Typography level="title-md">{title}</Typography>
            <Divider />
            <Stack gap={2} sx={{ mt: 1 }}>
                {err && (
                    <Alert color="danger" size="sm">
                        {err}
                    </Alert>
                )}
                <FormControl required>
                    <FormLabel>Name</FormLabel>
                    <Input
                        startDecorator={<PersonIcon fontSize="small" />}
                        placeholder="Joshua"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                </FormControl>
                <MultiChipInput
                    label="Telegram handles"
                    placeholder="@joshuathebest — press Enter to add"
                    icon={<TelegramIcon fontSize="small" />}
                    values={form.telegram_handles}
                    inputValue={form.tgInput}
                    onInputChange={(v) => setForm((f) => ({ ...f, tgInput: v }))}
                    onAdd={() => addItem('telegram_handles', 'tgInput', (v) => v.replace(/^@/, '').trim())}
                    onRemove={(v) => removeItem('telegram_handles', v)}
                    normalize={(v) => `@${v}`}
                />
                <MultiChipInput
                    label="Emails"
                    placeholder="joshua@numeo.ai — press Enter to add"
                    icon={<EmailIcon fontSize="small" />}
                    values={form.emails}
                    inputValue={form.emailInput}
                    onInputChange={(v) => setForm((f) => ({ ...f, emailInput: v }))}
                    onAdd={() => addItem('emails', 'emailInput', (v) => v.toLowerCase().trim())}
                    onRemove={(v) => removeItem('emails', v)}
                />
                <MultiChipInput
                    label="Phone numbers"
                    placeholder="+123456789 — press Enter to add"
                    icon={<PhoneIcon fontSize="small" />}
                    values={form.phone_numbers}
                    inputValue={form.phoneInput}
                    onInputChange={(v) => setForm((f) => ({ ...f, phoneInput: v }))}
                    onAdd={() => addItem('phone_numbers', 'phoneInput', (v) => v.trim())}
                    onRemove={(v) => removeItem('phone_numbers', v)}
                />
                <Stack direction="row" gap={1} justifyContent="flex-end">
                    <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} loading={saving}>
                        Save
                    </Button>
                </Stack>
            </Stack>
        </ModalDialog>
    )
}

function MemberRow({ member, onEdit, onDelete }: { member: CrmTeamMember; onEdit: () => void; onDelete: () => void }) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{ px: 1.5, py: 0.75, borderRadius: 'sm', '&:hover': { bgcolor: 'background.level1' }, transition: 'background 0.15s' }}
        >
            <Typography level="body-sm" fontWeight="md" sx={{ minWidth: 120, flexShrink: 0 }}>
                {member.name}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, flexWrap: 'wrap' }}>
                {member.telegram_handles.map((h) => (
                    <Stack key={h} direction="row" alignItems="center" gap={0.25}>
                        <TelegramIcon sx={{ fontSize: 12, color: 'text.tertiary', flexShrink: 0 }} />
                        <Typography level="body-xs" textColor="text.secondary">
                            @{h}
                        </Typography>
                    </Stack>
                ))}
                {member.emails.map((e) => (
                    <Stack key={e} direction="row" alignItems="center" gap={0.25}>
                        <EmailIcon sx={{ fontSize: 12, color: 'text.tertiary', flexShrink: 0 }} />
                        <Typography level="body-xs" textColor="text.secondary">
                            {e}
                        </Typography>
                    </Stack>
                ))}
                {member.phone_numbers.map((p) => (
                    <Stack key={p} direction="row" alignItems="center" gap={0.25}>
                        <PhoneIcon sx={{ fontSize: 12, color: 'text.tertiary', flexShrink: 0 }} />
                        <Typography level="body-xs" textColor="text.secondary">
                            {p}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
            <Stack direction="row" gap={0.25} sx={{ flexShrink: 0 }}>
                <IconButton size="sm" variant="plain" color="neutral" onClick={onEdit} sx={{ '--IconButton-size': '24px' }}>
                    <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton size="sm" variant="plain" color="danger" onClick={onDelete} sx={{ '--IconButton-size': '24px' }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Stack>
        </Stack>
    )
}

export function TeamMembersTab() {
    const { members, loading, error, create, update, remove } = useTeamMembers()
    const [showCreate, setShowCreate] = useState(false)
    const [editing, setEditing] = useState<CrmTeamMember | null>(null)

    const handleDelete = async (member: CrmTeamMember) => {
        if (!window.confirm(`Remove "${member.name}" from the team?`)) return
        await remove(member.id)
    }

    return (
        <Box sx={{ height: '100%', overflowY: 'auto' }}>
            <Stack gap={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography level="title-sm" textColor="text.secondary">
                        {members.length} {members.length === 1 ? 'member' : 'members'}
                    </Typography>
                    <Button size="sm" startDecorator={<AddIcon />} onClick={() => setShowCreate(true)}>
                        Add Member
                    </Button>
                </Stack>

                {error && (
                    <Alert color="danger" size="sm">
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size="sm" />
                    </Stack>
                )}

                {!loading && members.length === 0 && (
                    <Typography level="body-sm" textColor="text.tertiary" sx={{ py: 2, textAlign: 'center' }}>
                        No team members yet.
                    </Typography>
                )}

                {members.length > 0 && (
                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                        {members.map((m, i) => (
                            <Box key={m.id}>
                                {i > 0 && <Divider />}
                                <MemberRow member={m} onEdit={() => setEditing(m)} onDelete={() => handleDelete(m)} />
                            </Box>
                        ))}
                    </Sheet>
                )}
            </Stack>

            <Modal open={showCreate} onClose={() => setShowCreate(false)}>
                <MemberForm title="Add Team Member" initial={emptyForm()} onSave={create} onClose={() => setShowCreate(false)} />
            </Modal>

            <Modal open={!!editing} onClose={() => setEditing(null)}>
                {editing ? (
                    <MemberForm
                        title="Edit Team Member"
                        initial={memberToForm(editing)}
                        onSave={(input) => update(editing.id, input)}
                        onClose={() => setEditing(null)}
                    />
                ) : (
                    <Box />
                )}
            </Modal>
        </Box>
    )
}
